import _ from 'lodash';
import { degreesToRadians } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist/webpack';
import React, { useEffect, useRef, useState } from 'react';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DraggableProvided,
  DroppableProvided,
  DropResult,
} from 'react-beautiful-dnd';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';

import backArrow from '../assets/img/pdf-tool/svg/back.svg';
import {
  rotatePDF,
  selectErrorMessage,
  selectFilesToMerge,
  selectSortDescending,
  setFileOrder,
  toggleMergeSelect,
} from '../pdf-tool-manage/pdf-slice';
import { useAppDispatch } from '../store/store';
import { Toolbar } from '../toolbar/toolbar-component';

import { MergePanelComponent } from './pdf-tool-merge-panel-component';

require('./pdf-tool-merge-tool.scss');

export interface PdfToolManageStateProps {}

export interface PdfToolManageDispatchProps {}

export interface PdfToolManageOwnProps {}

export interface PdfToolManageProps
  extends PdfToolManageStateProps,
    PdfToolManageDispatchProps,
    PdfToolManageOwnProps {
  history: any;
}

function drawImage(
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  x: number,
  y: number,
  scale: number,
  rotation: number,
) {
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  ctx.setTransform(scale, 0, 0, scale, x, y); // sets scale and origin
  ctx.rotate(rotation);
  ctx.drawImage(image, -image.width / 2, -image.height / 2);
}

const PdfPageCard = ({ file, index }) => {
  const dispatch = useAppDispatch();

  const fileNameTruncated = (name: string) => {
    if (name.length > 15) {
      return name.substring(0, 15) + '...';
    }
    return name;
  };

  const handleRotate = (e: React.MouseEvent<HTMLDivElement>) => {
    // Rotate the first page by -90 degrees
    var image = new Image();

    const canvas: any = document.getElementById(
      `canvas-page-${file.file.modified}`,
    );
    image.src = canvas.toDataURL();
    const context = canvas.getContext('2d');
    const rotation = file.rotateDegrees - 90;
    image.onload = function () {
      drawImage(
        context,
        image,
        context.canvas.width / 2,
        context.canvas.height / 2,
        1,
        degreesToRadians(rotation),
      );
    };

    dispatch(rotatePDF(file));
  };

  const handleRemove = (e: React.MouseEvent<HTMLDivElement>) => {
    dispatch(toggleMergeSelect(file));
  };

  return (
    <Draggable draggableId={`${index}`} index={index}>
      {(provided: DraggableProvided) => (
        <div
          className="l-merge-tool__thumbnail-container"
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
        >
          <div className="l-merge-tool__thumbnail__actions">
            <div className="l-merge-tool__thumbnail__bg"></div>
            <div
              className="l-merge-tool__thumbnail__action l-merge-tool__thumbnail__action--rotate"
              onClick={(e) => handleRotate(e)}
            ></div>
            <div
              className="l-merge-tool__thumbnail__action l-merge-tool__thumbnail__action--remove"
              onClick={(e) => handleRemove(e)}
            ></div>
          </div>
          <div className="l-merge-tool__thumbnail">
            <div className="l-merge-tool__thumbnail__selection-ring">
              <canvas
                id={`canvas-page-${file.file.modified}`}
                className="l-merge-tool__thumbnail__canvas"
              ></canvas>
            </div>
          </div>
          <div
            className="l-merge-tool__thumbnail__name"
            data-tip={file.file.name}
          >
            {fileNameTruncated(file.file.name)}
          </div>
        </div>
      )}
    </Draggable>
  );
};

export const MergeToolComponent: React.FC<PdfToolManageProps> = ({
  history,
}) => {
  const [draggedCounter, setDraggedCounter] = useState(0);
  const isFirstRender = useRef(true);

  const filesToMerge = useSelector(selectFilesToMerge);
  const [finalFiles, setFinalFiles] = useState(filesToMerge);

  const sortDescending = useSelector(selectSortDescending);
  const errorMessage = useSelector(selectErrorMessage);

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!filesToMerge.length) {
      history.push('/pdf-tool/merge/');
    }
  }, [filesToMerge.length]);

  useEffect(() => {
    const sortedByOrder = _.sortBy(filesToMerge, (x) => x.order);
    setFinalFiles(sortedByOrder);
  }, [filesToMerge.length]);

  useEffect(() => {
    // Skip sorting by name on initial mount - preserve selection order
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const sortedFiles = _.orderBy(
      filesToMerge,
      [(x) => x.file.name.toLowerCase()],
      [sortDescending ? 'desc' : 'asc']
    );
    setFinalFiles(sortedFiles);
    setDraggedCounter(0);
  }, [sortDescending]);

  useEffect(() => {
    finalFiles.forEach((f, i) => {
      dispatch(
        setFileOrder({
          file: f,
          order: i,
        }),
      );
    });
  }, [finalFiles]);

  useEffect(() => {
    console.log('Updating file thumbnails');
    filesToMerge.forEach((file) => {
      const url = `/content/api/v2/mounts/${file.file.mountId}/files/get?path=${encodeURIComponent(file.file.path)}`;

      const loadingTask = pdfjsLib.getDocument(url);
      loadingTask.promise.then(
        (pdf) => {
          console.log('PDF loaded');
          const firstPagePromise = pdf.getPage(1);

          firstPagePromise.then((firstPage) => {
            const pagesCount = pdf.numPages;
            const viewport = firstPage.getViewport({ scale: 1 });
            const container = document.getElementById('mainContainer');
            const thumbnailsTemp = [];
            for (let pageNum = 1; pageNum <= pagesCount; pageNum += 1) {
              let thumbnail = {
                container: container,
                id: pageNum,
                defaultViewport: Object.assign({}, viewport),
                div: undefined,
              };

              thumbnailsTemp.push(thumbnail);
            }
          });

          pdf.getPage(1).then((page) => {
            const viewport = page.getViewport({ scale: 1 });

            // Prepare canvas using PDF page dimensions
            const canvas: any = document.getElementById(
              `canvas-page-${file.file.modified}`,
            );
            const context = canvas.getContext('2d');
            canvas.height = viewport.height;
            canvas.width = viewport.width;

            // Render PDF page into canvas context
            const renderContext = {
              canvasContext: context,
              viewport: viewport,
            };
            page.render(renderContext).promise.then(() => {
              const rotation = file.rotateDegrees;
              if (rotation !== 0) {
                var image = new Image();
                image.src = canvas.toDataURL();
                image.onload = function () {
                  drawImage(
                    context,
                    image,
                    context.canvas.width / 2,
                    context.canvas.height / 2,
                    1,
                    degreesToRadians(rotation),
                  );
                };
              }
            });
          });
        },
        (reason) => {
          // PDF loading error
          console.error(reason);
        },
      );
    });
  }, [finalFiles.length, sortDescending, draggedCounter]);

  useEffect(() => {
    // Force showing scrollbar
    document.body.classList.add('l-body-scroll-visible');
    return () => {
      document.body.classList.remove('l-body-scroll-visible');
    };
  }, []);

  const onDragEnd = (result: DropResult) => {
    const { destination, source } = result;

    if (!destination) {
      return;
    }

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const newFiles = [...finalFiles];
    const [movedFile] = newFiles.splice(source.index, 1);
    newFiles.splice(destination.index, 0, movedFile);

    setFinalFiles(newFiles);
    setDraggedCounter((c) => c + 1);
  };

  return (
    <DragDropContext onDragEnd={(r) => onDragEnd(r)}>
      <div className="l-merge-tool">
        <Toolbar>
          <div className="l-toolbar__left">
            <Link to="/pdf-tool/merge/" className="pdf-tool-split__navbar-left">
              <div className="l-toolbar__icon">
                <img src={backArrow} alt="Back arrow"></img>
              </div>
            </Link>
            <div className="l-toolbar__title">Merge</div>
          </div>
          <div className="l-toolbar__right">
            <Link to="/pdf-tool/merge/" className="l-toolbar__link">
              Add new files
            </Link>
          </div>
        </Toolbar>
        <ReactTooltip effect="solid" />
        <Droppable droppableId={'somedropbid'} direction="horizontal">
          {(provided: DroppableProvided) => (
            <div
              className="l-merge-tool__container"
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              { errorMessage &&
                <div className='l-merge-tool__error'>{errorMessage}</div>
              }
              {finalFiles.map((f, i) => (
                <PdfPageCard file={f} index={i} key={`pthumb-${i + 1}`} />
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
        <MergePanelComponent />
      </div>
    </DragDropContext>
  );
};
